import { useCallback, useMemo, useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { graphQLErrorMessage, FenToYuan, getMallLimitText } from '~/utils'
import {
  mallPreSettlement,
  mallPreSettlementVariables,
  MallBuyTypes,
  mallPreSettlement_settlementModule_preSettlement,
  mallCreateOrderPay,
  mallCreateOrderPayVariables,
  PayItemInput,
  mallPreSettlement_settlementModule_preSettlement_orderSkuList,
  mallPreSettlement_settlementModule_preSettlement_orderSkuList_specList,
  mallCreateOrderPay_orderModule_createOrderAndPay,
} from '~/graphqls/types'
import { MALL_CREATE_ORDER_PAY, MALL_PRE_SETTLEMENT } from '~/graphqls/gqls'
import { MyBtn } from '~/components/ui/MyBtn'
import { message } from 'antd'
import { MyModal } from '~/components/ui/MyModal'
import { MyIcon } from '~/components/ui/MyIcon'
import { Input } from 'antd'
import { ProductPrice } from '../module/ProductPrice'
import { PaymentWay } from '../module/PaymentWay'
import { Address } from '../module/Address'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styled from 'styled-components'
import AddressList from '../address/AddrListModal'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { MyImage } from '~/components/ui/MyImage'
import { ScanPayModal } from '~/components/pages/mall/order/ScanPayModal'
import OrderSuccessModal from '~/components/pages/mall/order/OrderSuccessModal'
import { useOrder } from './hooks'

const StyledContent = styled.div<{ isDarkMode: boolean }>`
  .ant-input {
    background-color: ${props =>
      props.isDarkMode ? props.theme.dark1 : '#fff'};
    color: ${props => (props.isDarkMode ? '#B1B5C3' : props.theme.dark1)};
    border-color: ${props =>
      props.isDarkMode ? props.theme.dark3 : props.theme.divider1};
  }
`

export const CheckoutModal: React.FC<{
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  payItemList: PayItemInput[]
  aid?: number
  isDarkMode?: boolean
  isCart?: boolean
  source?: MallBuyTypes
}> = ({
  visible,
  setVisible,
  payItemList,
  aid,
  isDarkMode = false,
  isCart,
  source,
}) => {
  const [msg, setMsg] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [addrVisible, setAddrVisible] = useState(false)
  const [addrId, setAddrId] = useState<number>(aid as number)
  const [scanPayVisible, setScanPayVisible] = useState(false)
  const [paySuccessVisible, setPaySuccessVisible] = useState(false)
  const [mounted, setMounted] = useState(false)
  // 三方支付信息
  const [payInfo, setPayInfo] =
    useState<mallCreateOrderPay_orderModule_createOrderAndPay>()

  const [orderData, setOrderData] =
    useState<mallPreSettlement_settlementModule_preSettlement | null>(null)

  const getPreSettleData = useImperativeQuery<
    mallPreSettlement,
    mallPreSettlementVariables
  >(MALL_PRE_SETTLEMENT)
  const { genSpec } = useOrder()

  /** 获取预结算信息 */
  const fetchPreSettleData = useCallback(
    async addressId => {
      try {
        const params: mallPreSettlementVariables = {
          payItemList,
        }
        if (addressId) params.addressId = Number(addressId)
        if (isCart) params.isCart = isCart
        const res = await getPreSettleData(params)
        setOrderData(
          res?.data?.settlementModule
            ?.preSettlement as mallPreSettlement_settlementModule_preSettlement
        )
        setMounted(true)
      } catch (err) {
        message.error(graphQLErrorMessage(err) || '获取预结算信息失败，请重试')
      }
    },
    [payItemList, aid, isCart]
  )
  useEffect(() => {
    if (visible) {
      fetchPreSettleData(aid)
    } else {
      setOrderData(null)
    }
  }, [visible, aid])

  /** 支付 */
  const [payAction] = useMutation<
    mallCreateOrderPay,
    mallCreateOrderPayVariables
  >(MALL_CREATE_ORDER_PAY)

  //下单来源
  const sourceFromCart = useMemo(() => {
    return source === MallBuyTypes.CART
  }, [source])

  const gotoAddress = useCallback(() => {
    setAddrVisible(true)
  }, [addrId])
  const summitDisabled = useMemo(() => {
    return (
      !orderData?.receiverInfo?.addressId ||
      !!orderData?.orderSkuList?.filter(it => !!it?.limitStatus)?.length
    )
  }, [orderData])
  // 提交订单
  const confirmOrder = useCallback(() => {
    if (isLoading) return
    setIsLoading(true)
    payAction({
      variables: {
        addressId: orderData?.receiverInfo?.addressId,
        payItemList,
        sourceItem: !!sourceFromCart ? 2 : 1,
        totalFee: orderData?.totalFee,
        // 二维码方式
        payType: 4,
        remark: (msg || '').replace(
          /(\ud83c[\udf00-\udfff])|(\ud83d[\udc00-\ude4f\ude80-\udeff])|[\u2600-\u2B55]/g,
          ''
        ),
      },
    })
      .then(res => {
        setPayInfo(
          res?.data?.orderModule
            ?.createOrderAndPay as mallCreateOrderPay_orderModule_createOrderAndPay
        )
        if (res?.data?.orderModule?.createOrderAndPay?.needPay) {
          setScanPayVisible(true)
        } else {
          setPaySuccessVisible(true)
        }
      })
      .catch(err => {
        message.error({
          content: graphQLErrorMessage(err) || '购买失败，请重试',
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isLoading, sourceFromCart, orderData, msg, payItemList])

  return (
    <>
      {mounted && (
        <MyModal
          visible={visible}
          title="订单预结算"
          onCancel={() => setVisible(false)}
          maskStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}
          width={448}
          footer={
            <MyBtn
              className="w-full mb-3"
              type="primary"
              onClick={confirmOrder}
              disabled={summitDisabled}
            >
              提交订单
            </MyBtn>
          }
          bodyStyle={{ maxHeight: 600, overflowY: 'auto' }}
          dark={isDarkMode}
          hiddenBorderTop
          centered
        >
          {orderData ? (
            <StyledContent isDarkMode={isDarkMode} className="relative">
              {/* 收货地址 */}
              <div
                className={`border-rounded relative mb-6 !pr-8 cursor-pointer ${
                  isDarkMode ? '!bg-neutral-100 !border-neutral-300' : ''
                }`}
                onClick={gotoAddress}
              >
                {orderData?.receiverInfo?.addressId ? (
                  <Address orderData={orderData} isDarkMode={isDarkMode} />
                ) : (
                  <div className="!text-neutral-400">
                    <div className="!text-neutral-400 text-sm font-medium">
                      请添加收货地址
                    </div>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400">
                      <MyIcon icon="arrow-right-simple" className="text-lg" />
                    </div>
                  </div>
                )}
              </div>
              {/* 商品 信息 */}
              <div className="mb-6">
                {orderData?.orderSkuList?.map(
                  (
                    item: mallPreSettlement_settlementModule_preSettlement_orderSkuList | null,
                    index: number
                  ) => (
                    <div
                      className="flex items-start justify-start mb-6"
                      key={index}
                    >
                      <div className="w-20 h-20 rounded-2xl border-2 border-neutral-600 overflow-hidden mr-3">
                        <MyImage
                          className="w-full h-full"
                          src={item?.imageList?.[0] || ''}
                        />
                      </div>
                      <div className="flex-1">
                        <div
                          className={`text-base font-medium break-all line-clamp-2 ${
                            isDarkMode ? 'text-neutral-900' : 'text-neutral-100'
                          }`}
                        >
                          {item?.name}
                        </div>
                        <div className="text-sm font-normal text-neutral-400 mt-1 flex justify-between items-start">
                          <span>
                            {genSpec(
                              item?.specList as mallPreSettlement_settlementModule_preSettlement_orderSkuList_specList[]
                            )}
                          </span>
                          <span className="ml-1.5 whitespace-nowrap">
                            x {item?.num}
                          </span>
                        </div>
                        <div className="text-error text-sm font-medium mt-1 flex justify-between items-center">
                          <span>¥{FenToYuan(item?.price)}</span>
                          {!!item?.limitStatus && (
                            <div className="text-xs px-3 py-0.5 bg-error bg-opacity-20 rounded-3xl">
                              {getMallLimitText(item?.limitStatus)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
              {/* 留言信息 */}
              <div className="mb-6">
                <div className="text-sm text-neutral-400 font-medium mb-3">
                  买家留言
                </div>
                <Input
                  placeholder="最多 50 个字，如需电子发票请填写邮箱"
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  maxLength={50}
                  className="!rounded-xl !py-3 !px-4 !border-2"
                />
              </div>
              {/* 商品金额 */}
              <ProductPrice orderData={orderData} isDarkMode={isDarkMode} />
              {/* 支付方式 */}
              <PaymentWay isDarkMode={isDarkMode} />
            </StyledContent>
          ) : (
            <MyEmpty />
          )}

          {addrVisible && (
            <AddressList
              visible={addrVisible}
              setVisible={setAddrVisible}
              isDarkMode={isDarkMode}
              handleBackTo={val => {
                setAddrId(val)
                fetchPreSettleData(val as number)
              }}
              aid={addrId}
            />
          )}

          {scanPayVisible && (
            <ScanPayModal
              isDarkMode={isDarkMode}
              payInfo={payInfo}
              visible={scanPayVisible}
              setVisible={setScanPayVisible}
              amount={orderData?.realFee}
              needLock={true}
            />
          )}
          {paySuccessVisible && (
            <OrderSuccessModal
              isDarkMode={isDarkMode}
              visible={paySuccessVisible}
              setVisible={setPaySuccessVisible}
              payInfo={payInfo}
            />
          )}
        </MyModal>
      )}
    </>
  )
}
